import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Search as SearchIcon,
  Plus,
  Upload,
  Trash2,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  FileType2,
  Link2,
  Quote,
  Clock,
  Mic,
  ChefHat,
  Heart,
  BookOpen,
  Loader2,
  HardDrive,
} from "lucide-react";
import { vaultApi } from "../api/vault";

// ---------- helpers ----------

const KIND_ICONS = {
  text: FileText,
  image: ImageIcon,
  audio: Music,
  video: Video,
  document: FileType2,
  link: Link2,
  structured: BookOpen,
};

const CATEGORY_ICONS = {
  note: FileText,
  photo: ImageIcon,
  audio: Music,
  video: Video,
  document: FileType2,
  research: BookOpen,
  recipe: ChefHat,
  memory: Heart,
  timeline_event: Clock,
  interview: Mic,
  quote: Quote,
  link: Link2,
};

const formatBytes = (n) => {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};

const inferKindFromMime = (mime) => {
  if (!mime) return "document";
  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("audio/")) return "audio";
  if (mime.startsWith("video/")) return "video";
  if (mime === "application/pdf") return "document";
  if (mime.startsWith("text/")) return "document";
  return "document";
};

// ---------- main page ----------

export default function Vault() {
  const navigate = useNavigate();

  const [registry, setRegistry] = useState({ kinds: [], categories: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [usage, setUsage] = useState({ total_bytes: 0, count: 0 });
  const [loading, setLoading] = useState(true);

  // filters
  const [q, setQ] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterKind, setFilterKind] = useState("all");

  // create modals
  const [showNewNote, setShowNewNote] = useState(false);
  const [showUpload, setShowUpload] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const filters = { limit: 100 };
      if (q) filters.q = q;
      if (filterCategory !== "all") filters.category = filterCategory;
      if (filterKind !== "all") filters.kind = filterKind;
      const [list, u] = await Promise.all([vaultApi.list(filters), vaultApi.usage()]);
      setItems(list.items || []);
      setTotal(list.total || 0);
      setUsage(u);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Failed to load vault: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [q, filterCategory, filterKind]);

  useEffect(() => {
    vaultApi.categories().then(setRegistry).catch(() => {});
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enabledCategories = useMemo(
    () => (registry.categories || []).filter((c) => c.enabled),
    [registry.categories],
  );

  const filtersActive = useMemo(
    () => Boolean(q || filterCategory !== "all" || filterKind !== "all"),
    [q, filterCategory, filterKind],
  );

  const clearFilters = () => {
    setQ("");
    setFilterCategory("all");
    setFilterKind("all");
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this vault item? It will be hidden from search.")) return;
    try {
      await vaultApi.remove(id);
      toast.success("Item deleted");
      refresh();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="vault-page">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/60 backdrop-blur sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/dashboard")}
              data-testid="vault-back-btn"
              className="shrink-0"
            >
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-2xl font-bold tracking-tight truncate">Story Vault</h1>
              <p className="text-xs text-slate-400 hidden sm:block">
                Universal repository for notes, media, and source material.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Badge variant="outline" className="border-slate-700 text-slate-300 text-[10px] sm:text-xs whitespace-nowrap" data-testid="vault-usage-badge">
              <HardDrive className="w-3 h-3 mr-1 sm:mr-1.5" />
              {formatBytes(usage.total_bytes)} • {usage.count}
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
        {/* Toolbar */}
        <div className="flex flex-col md:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder="Search title, body, tags…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-slate-900 border-slate-700"
              data-testid="vault-search-input"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="md:w-44 bg-slate-900 border-slate-700" data-testid="vault-filter-category">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {enabledCategories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.display_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterKind} onValueChange={setFilterKind}>
            <SelectTrigger className="md:w-36 bg-slate-900 border-slate-700" data-testid="vault-filter-kind">
              <SelectValue placeholder="All kinds" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              {(registry.kinds || []).map((k) => (
                <SelectItem key={k} value={k}>
                  {k}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Dialog open={showNewNote} onOpenChange={setShowNewNote}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-cyan-500/40 text-cyan-300" data-testid="vault-new-note-btn">
                <Plus className="w-4 h-4 mr-2" />
                New note
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
              <DialogHeader>
                <DialogTitle>New text item</DialogTitle>
                <DialogDescription className="text-slate-400 text-xs">
                  Save a note, quote, link, or memory to your vault.
                </DialogDescription>
              </DialogHeader>
              <NewTextItemForm
                categories={enabledCategories}
                onCreated={() => {
                  setShowNewNote(false);
                  refresh();
                }}
              />
            </DialogContent>
          </Dialog>

          <Dialog open={showUpload} onOpenChange={setShowUpload}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500" data-testid="vault-upload-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
              <DialogHeader>
                <DialogTitle>Upload file</DialogTitle>
                <DialogDescription className="text-slate-400 text-xs">
                  Photos, audio, video, PDFs, and documents up to 95 MB.
                </DialogDescription>
              </DialogHeader>
              <UploadForm
                categories={enabledCategories}
                onUploaded={() => {
                  setShowUpload(false);
                  refresh();
                }}
              />
            </DialogContent>
          </Dialog>
        </div>

        {/* Items grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Loading vault…
          </div>
        ) : items.length === 0 ? (
          filtersActive && usage.count > 0 ? (
            <NoMatchesState onClear={clearFilters} />
          ) : (
            <EmptyState />
          )
        ) : (
          <>
            <p className="text-xs text-slate-500 mb-3" data-testid="vault-result-count">
              {total} item{total === 1 ? "" : "s"}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="vault-items-grid">
              {items.map((it) => (
                <VaultItemCard key={it.id} item={it} onDelete={onDelete} />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------- subcomponents ----------

function EmptyState() {
  return (
    <div className="text-center py-20 border border-dashed border-slate-800 rounded-lg" data-testid="vault-empty-state">
      <BookOpen className="w-12 h-12 mx-auto text-slate-600 mb-4" />
      <h3 className="text-lg font-semibold text-slate-300">Your vault is empty</h3>
      <p className="text-sm text-slate-500 mt-2">
        Save notes, photos, audio, documents, and links here. Anything you store can later
        feed your books, recipes, and life stories.
      </p>
    </div>
  );
}

function NoMatchesState({ onClear }) {
  return (
    <div className="text-center py-20 border border-dashed border-slate-800 rounded-lg" data-testid="vault-no-results">
      <SearchIcon className="w-12 h-12 mx-auto text-slate-600 mb-4" />
      <h3 className="text-lg font-semibold text-slate-300">No items match your filters</h3>
      <p className="text-sm text-slate-500 mt-2 mb-4">
        Your vault still has saved items — they just don&apos;t match the current search.
      </p>
      <Button variant="outline" size="sm" onClick={onClear} data-testid="vault-clear-filters">
        Clear filters
      </Button>
    </div>
  );
}

function VaultItemCard({ item, onDelete }) {
  const KIcon = KIND_ICONS[item.kind] || FileText;
  const CIcon = CATEGORY_ICONS[item.category] || FileText;
  const fileUrl = item.file_ref ? vaultApi.downloadUrl(item.id) : null;

  return (
    <div
      className="group bg-slate-900 border border-slate-800 rounded-lg p-4 hover:border-cyan-500/40 transition-colors"
      data-testid={`vault-item-${item.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <CIcon className="w-4 h-4 text-cyan-400 flex-shrink-0" />
          <span className="text-xs text-slate-400 truncate">{item.category}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-slate-500 hover:text-red-400"
          onClick={() => onDelete(item.id)}
          data-testid={`vault-delete-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <h3 className="font-semibold text-slate-100 mb-2 line-clamp-2" data-testid={`vault-item-title-${item.id}`}>
        {item.title || "(untitled)"}
      </h3>

      {/* Media preview */}
      {item.kind === "image" && fileUrl && (
        <img
          src={fileUrl}
          alt={item.title}
          className="w-full h-40 object-cover rounded mb-3 bg-slate-800"
          data-testid={`vault-image-${item.id}`}
        />
      )}
      {item.kind === "audio" && fileUrl && (
        <audio src={fileUrl} controls className="w-full mb-3" data-testid={`vault-audio-${item.id}`} />
      )}
      {item.kind === "video" && fileUrl && (
        <video src={fileUrl} controls className="w-full max-h-48 rounded mb-3 bg-black" data-testid={`vault-video-${item.id}`} />
      )}

      {item.body_text && (
        <p className="text-sm text-slate-400 line-clamp-3 mb-3 whitespace-pre-wrap">{item.body_text}</p>
      )}

      <div className="flex items-center justify-between text-xs text-slate-500 mt-3 pt-3 border-t border-slate-800">
        <div className="flex items-center gap-1">
          <KIcon className="w-3 h-3" />
          <span>{item.kind}</span>
        </div>
        {item.storage_size_bytes > 0 && <span>{formatBytes(item.storage_size_bytes)}</span>}
      </div>

      {(item.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {item.tags.slice(0, 4).map((t) => (
            <Badge key={t} variant="outline" className="text-[10px] border-slate-700 text-slate-400">
              {t}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function NewTextItemForm({ categories, onCreated }) {
  const [category, setCategory] = useState("note");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);

  // Map category → kind based on registry default + safe fallback to text/link
  const kindFor = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    if (!cat) return "text";
    return cat.default_kind === "structured" ? "text" : cat.default_kind;
  };

  const submit = async () => {
    if (!body.trim() && !title.trim()) {
      toast.error("Give it a title or some content");
      return;
    }
    const kind = category === "link" ? "link" : "text";
    setBusy(true);
    try {
      await vaultApi.createText({
        kind,
        category,
        title,
        body_text: body,
        tags: tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        source: "paste",
      });
      toast.success("Saved to vault");
      onCreated();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  // Only categories whose default_kind is text/link are valid for this quick form
  const eligible = categories.filter(
    (c) => c.default_kind === "text" || c.default_kind === "link",
  );

  // ensure selected category stays valid
  useEffect(() => {
    if (!eligible.find((c) => c.id === category) && eligible.length) {
      setCategory(eligible[0].id);
    }
  }, [eligible, category]);

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="vault-newnote-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {eligible.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Title</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="A short, searchable title"
          data-testid="vault-newnote-title"
        />
      </div>
      <div>
        <Label className="text-xs">{category === "link" ? "URL" : "Content"}</Label>
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={6}
          placeholder={category === "link" ? "https://…" : "Write your note here…"}
          data-testid="vault-newnote-body"
        />
      </div>
      <div>
        <Label className="text-xs">Tags (comma-separated)</Label>
        <Input
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="research, family, draft"
          data-testid="vault-newnote-tags"
        />
      </div>
      <Button onClick={submit} disabled={busy} className="w-full" data-testid="vault-newnote-save">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
        Save to vault
      </Button>
      <p className="text-[10px] text-slate-500">
        Use “Upload” for photos, audio, video, and PDFs.
      </p>
    </div>
  );
}

function UploadForm({ categories, onUploaded }) {
  const [file, setFile] = useState(null);
  const [category, setCategory] = useState("photo");
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);

  // category options for uploads = those that allow non-text kinds
  const uploadableCats = categories.filter((c) =>
    (c.allowed_kinds || []).some((k) => k !== "text" && k !== "link" && k !== "structured"),
  );

  useEffect(() => {
    if (!uploadableCats.find((c) => c.id === category) && uploadableCats.length) {
      setCategory(uploadableCats[0].id);
    }
  }, [uploadableCats, category]);

  const submit = async () => {
    if (!file) {
      toast.error("Pick a file first");
      return;
    }
    const kind = inferKindFromMime(file.type);
    const cat = categories.find((c) => c.id === category);
    if (cat && !cat.allowed_kinds.includes(kind)) {
      toast.error(
        `${cat.display_name} doesn't accept ${kind} files. Allowed: ${cat.allowed_kinds.join(", ")}`,
      );
      return;
    }
    setBusy(true);
    try {
      await vaultApi.uploadFile({
        file,
        kind,
        category,
        title: title || file.name,
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      toast.success("Uploaded");
      onUploaded();
    } catch (e) {
      toast.error(`Upload failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">File (max 95 MB)</Label>
        <Input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="vault-upload-file"
        />
        {file && (
          <p className="text-[10px] text-slate-500 mt-1">
            {file.name} • {formatBytes(file.size)} • {file.type || "unknown"}
          </p>
        )}
      </div>
      <div>
        <Label className="text-xs">Category</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="vault-upload-category">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {uploadableCats.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.display_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs">Title</Label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Defaults to filename if empty"
          data-testid="vault-upload-title"
        />
      </div>
      <div>
        <Label className="text-xs">Tags (comma-separated)</Label>
        <Input value={tags} onChange={(e) => setTags(e.target.value)} data-testid="vault-upload-tags" />
      </div>
      <Button onClick={submit} disabled={busy || !file} className="w-full" data-testid="vault-upload-save">
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        Upload to vault
      </Button>
    </div>
  );
}
